import type { GetFormCustomisationQuery } from '@risksmart-app/web-graphql-client/generated/graphql';

import { scopeToServerId } from '../conditionPropertyKeys';

/**
 * Case-insensitive membership for a set of hidden field ids.
 *
 * The hidden set is keyed by the persisted `form_field_configuration.FieldId`,
 * but consumers ask for it under whichever casing they hold: the rendered form
 * uses the canonical form-value `FieldId` (`Description`) while the form editor
 * persists the field's lowercase React key (`description`). Casing is a
 * per-entity convention — `third_party` is legitimately all-lowercase while
 * risk/control are PascalCase — so neither side can be declared "the" casing.
 * Within a single form a field has exactly one identity regardless of which
 * writer cased it, so comparing case-insensitively is the correct invariant.
 *
 * The exact-hit fast path covers the overwhelmingly common case (both sides
 * already agree); the scan only runs for a genuine casing mismatch, over a set
 * that holds at most the fields whose conditions currently evaluate false.
 */
export const hasFieldId = (
  fieldIds: Set<string>,
  fieldId: string | null | undefined
): boolean => {
  if (!fieldId) {
    return false;
  }
  if (fieldIds.has(fieldId)) {
    return true;
  }

  const lower = fieldId.toLowerCase();

  for (const candidate of fieldIds) {
    if (candidate.toLowerCase() === lower) {
      return true;
    }
  }

  return false;
};

export const getHiddenFieldIds = ({
  hiddenFields,
  hiddenSections,
  formSections,
}: {
  formSections: GetFormCustomisationQuery['form_section_configuration'] | null;
  hiddenFields: Set<string>;
  hiddenSections: Set<string>;
}): Set<string> => {
  if (hiddenSections.size === 0) {
    return hiddenFields;
  }

  const hiddenFieldIds = new Set(hiddenFields);

  for (const section of formSections ?? []) {
    if (!hiddenSections.has(section.SectionId)) {
      continue;
    }
    for (const field of section.fields ?? []) {
      // section rows key custom attrs as scope, the hidden set as server format
      hiddenFieldIds.add(scopeToServerId(field.FieldId));
    }
  }

  return hiddenFieldIds;
};
